import { useEffect, useState } from "react";
import { useLang, ensureSessionId } from "@/context/LanguageContext";
import { api, API, trackEvent } from "@/lib/api";
import { FileText, Clock, Download } from "lucide-react";
import PreToolDisclaimer from "@/components/PreToolDisclaimer";

export default function Toolkit() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [version, setVersion] = useState("1.0");

  useEffect(() => {
    api.get("/toolkit").then((r) => {
      setItems(r.data.resources || []);
      if (r.data.version) setVersion(r.data.version);
    });
  }, []);

  const onDownload = (r) => {
    trackEvent("toolkit_downloaded", { tool: r.id });
    const sid = ensureSessionId();
    window.open(`${API}/toolkit/${r.id}/download?language=${lang}&session_id=${sid}`, "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20" data-testid="toolkit-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-4xl sm:text-5xl font-bold">{t.toolkit.title}</h1>
        <span className="text-xs uppercase tracking-widest px-3 py-1 rounded-full border border-blue-500/40 text-blue-300 bg-blue-500/10"
              data-testid="toolkit-version">
          MIFT Professional Toolkit — Version {version}
        </span>
      </div>
      <p className="mt-4 text-slate-300 max-w-3xl">{t.toolkit.sub}</p>

      <PreToolDisclaimer />

      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((r) => {
          const approved = (r.approved_languages || []).includes(lang);
          return (
            <div key={r.id} className="card-mift p-6 flex flex-col" data-testid={`toolkit-${r.id}`}>
              <div className="flex items-center justify-between">
                <FileText className="w-6 h-6 text-blue-300" />
                <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-slate-700 text-slate-400">
                  {r.code}
                </span>
              </div>
              <div className="mt-4 font-semibold text-slate-100 leading-snug">
                {r.title[lang] || r.title.en}
              </div>
              <div className="mt-2 text-xs text-slate-500">MIFT · v{version} · {lang.toUpperCase()}</div>

              {approved ? (
                <button
                  onClick={() => onDownload(r)}
                  data-testid={`toolkit-download-${r.id}`}
                  className="mt-6 btn-primary inline-flex items-center gap-2 w-max"
                >
                  <Download className="w-4 h-4" /> {t.toolkit.download}
                </button>
              ) : (
                <>
                  <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border border-amber-500/30 bg-amber-500/5 text-amber-200 w-max"
                       data-testid={`toolkit-preparation-${r.id}`}>
                    <Clock className="w-3.5 h-3.5" /> {t.toolkit.in_preparation}
                  </div>
                  <p className="mt-3 text-xs text-slate-500 leading-relaxed">{t.toolkit.preparation_note}</p>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
